import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash } from '@phosphor-icons/react';
import { useEpisodeStore } from '@/state/episodeStore';
import { useSelectionStore } from '@/state/selectionStore';
import { BottomInput } from '@/components/BottomInput';
import { ImageVariants } from '@/components/scene-detail/ImageVariants';
import { VideoVariants } from '@/components/scene-detail/VideoVariants';
import {
  deleteScene,
  deleteSceneImage,
  deleteSceneVideo,
  patchScene,
} from '@/services/scenesApi';
import { runMutation } from '@/state/saveQueue';
import { loadEpisodeBundle, EpisodeAccessError } from '@/services/episodeApi';
import { toast } from '@/state/toastStore';
import type { SceneVideo } from '@/types';

export function ScenePage() {
  const { uuid, sceneId } = useParams<{ uuid: string; sceneId: string }>();
  const navigate = useNavigate();

  const scene = useEpisodeStore((s) =>
    sceneId ? s.scenes.find((sc) => sc.id === sceneId) ?? null : null,
  );
  const episodeLoaded = useEpisodeStore((s) => s.episode?.uuid === uuid);
  const isLoading = useEpisodeStore((s) => s.isLoading);
  const loadError = useEpisodeStore((s) => s.loadError);
  const setBundle = useEpisodeStore((s) => s.setBundle);
  const setLoading = useEpisodeStore((s) => s.setLoading);
  const setLoadError = useEpisodeStore((s) => s.setLoadError);
  const upsertScene = useEpisodeStore((s) => s.upsertScene);
  const upsertImage = useEpisodeStore((s) => s.upsertImage);
  const upsertVideo = useEpisodeStore((s) => s.upsertVideo);

  const activateScene = useSelectionStore((s) => s.activateScene);

  // Load the bundle if the user landed here directly (no prior visit to the
  // grid). EpisodePage usually populates the store, but a deep-linked
  // /episode/X/scene/Y URL needs to fetch on its own.
  useEffect(() => {
    if (!uuid || episodeLoaded || isLoading) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    loadEpisodeBundle(uuid)
      .then((bundle) => {
        if (!cancelled) setBundle(bundle);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof EpisodeAccessError) setLoadError('forbidden');
        else setLoadError(err instanceof Error ? err.message : 'Failed to load episode');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uuid, episodeLoaded, isLoading, setBundle, setLoading, setLoadError]);

  // Make this scene the active scene whenever the route lands here, so the
  // bottom input is scoped to it.
  useEffect(() => {
    if (scene) {
      activateScene(scene);
    }
    return () => {
      activateScene(null);
    };
  }, [scene?.id, activateScene]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading && !scene) {
    return (
      <main className="flex h-full flex-col items-center justify-center px-6 text-center">
        <p className="font-mono text-[12px] text-text-4">loading…</p>
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="flex h-full flex-col items-center justify-center px-6 text-center">
        <p className="font-mono text-[12px] text-err">load failed</p>
        <p className="mt-2 max-w-md text-[14px] text-text-3">{loadError}</p>
        <Link
          to={`/episode/${uuid ?? ''}`}
          className="mt-4 text-[13px] text-text-2 transition-colors hover:text-text-1"
        >
          ← Back to grid
        </Link>
      </main>
    );
  }

  if (!scene || !uuid) {
    return (
      <main className="flex h-full flex-col items-center justify-center px-6 text-center">
        <p className="font-mono text-[12px] text-text-4">scene not found</p>
        <Link
          to={`/episode/${uuid ?? ''}`}
          className="mt-4 text-[13px] text-text-2 transition-colors hover:text-text-1"
        >
          ← Back to grid
        </Link>
      </main>
    );
  }

  async function pinHero(imageId: string | null) {
    if (!scene || !uuid) return;
    const previous = scene.heroImageId;
    upsertScene({ ...scene, heroImageId: imageId, version: scene.version + 1 });
    try {
      const updated = await runMutation(
        {
          episodeUuid: uuid,
          endpoint: `/v2/scenes/${scene.id}`,
          method: 'PATCH',
          body: { heroImageId: imageId },
          expectedVersion: scene.version,
        },
        () => patchScene(uuid, scene.id, { heroImageId: imageId } as any, scene.version),
      );
      upsertScene(updated);
    } catch {
      upsertScene({ ...scene, heroImageId: previous });
    }
  }

  async function deleteImage(imageId: string) {
    if (!scene || !uuid) return;
    const image = useEpisodeStore.getState().images[imageId];
    if (!image) return;
    upsertImage({ ...image, deletedAt: Date.now() });
    try {
      await runMutation(
        {
          episodeUuid: uuid,
          endpoint: `/v2/scenes/${scene.id}/images/${imageId}`,
          method: 'DELETE',
          body: {},
        },
        () => deleteSceneImage(uuid, scene.id, imageId, image.version),
      );
    } catch {
      upsertImage({ ...image, deletedAt: null });
    }
  }

  async function deleteVideo(videoId: string) {
    if (!scene || !uuid) return;
    const video = useEpisodeStore.getState().videos[videoId];
    if (!video) return;
    upsertVideo({ ...video, deletedAt: Date.now() });
    try {
      await runMutation(
        {
          episodeUuid: uuid,
          endpoint: `/v2/scenes/${scene.id}/videos/${videoId}`,
          method: 'DELETE',
          body: {},
        },
        () => deleteSceneVideo(uuid, scene.id, videoId, video.version),
      );
    } catch {
      upsertVideo({ ...video, deletedAt: null });
    }
  }

  async function onDeleteScene() {
    if (!scene || !uuid) return;
    if (!confirm(`Delete scene ${String(scene.order).padStart(2, '0')}? Images and videos go with it.`)) return;
    const previous = scene;
    upsertScene({ ...scene, deletedAt: Date.now() });
    try {
      await runMutation(
        {
          episodeUuid: uuid,
          endpoint: `/v2/scenes/${scene.id}`,
          method: 'DELETE',
          body: {},
        },
        () => deleteScene(uuid, scene.id, scene.version),
      );
      toast.ok('Scene deleted.');
      navigate(`/episode/${uuid}`);
    } catch (err) {
      upsertScene(previous);
      toast.err(err instanceof Error ? err.message : 'Delete failed.');
    }
  }

  function extendVideo(video: SceneVideo) {
    if (!scene) return;
    // Switch to Video mode and prefill with extension intent
    useSelectionStore.getState().setAssetMode('video');
    activateScene(scene, {
      prompt: video.prompt,
      references: video.references,
      fresh: video.fresh,
    });
    // The actual generate call happens when the user submits — extend semantics
    // are added in the submit path of useGenerateVideo (Phase 4 wires the
    // extend button → generate flow end-to-end).
  }

  return (
    <div className="flex h-full flex-col">
      <Header
        sceneNumber={scene.order}
        prompt={scene.prompt}
        onBack={() => navigate(`/episode/${uuid}`)}
        onDelete={onDeleteScene}
      />

      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="mx-auto grid max-w-6xl gap-8 xl:grid-cols-2">
          <section>
            <h2 className="mb-3 text-[15px] font-semibold text-text-1">Images</h2>
            <ImageVariants
              scene={scene}
              episodeUuid={uuid}
              onPinHero={pinHero}
              onDelete={deleteImage}
            />
          </section>
          <section>
            <h2 className="mb-3 text-[15px] font-semibold text-text-1">Videos</h2>
            <VideoVariants
              scene={scene}
              episodeUuid={uuid}
              onExtend={extendVideo}
              onDelete={deleteVideo}
            />
          </section>
        </div>
      </div>

      <BottomInput episodeUuid={uuid} showAssetToggle />
    </div>
  );
}

function Header({
  sceneNumber,
  prompt,
  onBack,
  onDelete,
}: {
  sceneNumber: number;
  prompt: string;
  onBack: () => void;
  onDelete: () => void;
}) {
  return (
    <header className="flex h-20 shrink-0 items-center gap-5 border-b border-line-1 bg-surface-1 px-5 shadow-elev-2">
      <button
        type="button"
        onClick={onBack}
        aria-label="Back to grid"
        className="rounded-md p-1.5 text-text-3 transition-colors hover:bg-surface-2 hover:text-text-1"
      >
        <ArrowLeft size={20} />
      </button>
      <span className="font-mono text-[24px] font-semibold tabular-nums text-text-1">
        {String(sceneNumber).padStart(2, '0')}
      </span>
      <p className="min-w-0 flex-1 truncate text-[18px] text-text-2">
        {prompt || <span className="italic text-text-4">no prompt</span>}
      </p>
      <button
        type="button"
        onClick={onDelete}
        aria-label="Delete scene"
        className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-[12px] font-medium text-text-3 transition-colors hover:bg-err/15 hover:text-err"
      >
        <Trash size={14} />
        Delete
      </button>
    </header>
  );
}
